import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/client/lib/api-client'

export interface BacklinkSummary {
  totalBacklinks: number
  referringDomains: number
  referringIps: number
  domainRank: number
  spamScore: number
  nofollowDomains: number
  brokenBacklinks: number
}

export interface ReferringDomain {
  domain: string
  rank: number
  backlinks: number
  firstSeen: string
  lastSeen: string
  isLost: boolean
}

export interface AnchorData {
  anchor: string
  domains: number
  backlinks: number
}

/** Backlinks summary for a site */
export function useBacklinksSummary(siteId: string) {
  return useQuery({
    queryKey: ['backlinks', 'summary', siteId],
    queryFn: () => apiClient.get<{
      summary: BacklinkSummary
      configured: boolean
    }>(`/api/sites/${siteId}/backlinks/summary`),
    enabled: !!siteId,
    staleTime: 30 * 60 * 1000,
  })
}

/** Top referring domains */
export function useReferringDomains(siteId: string) {
  return useQuery({
    queryKey: ['backlinks', 'domains', siteId],
    queryFn: () => apiClient.get<{
      domains: ReferringDomain[]
    }>(`/api/sites/${siteId}/backlinks/domains`),
    enabled: !!siteId,
    staleTime: 30 * 60 * 1000,
  })
}

/** Backlink changes (new/lost domains between snapshots) */
export function useBacklinkChanges(siteId: string) {
  return useQuery({
    queryKey: ['backlinks', 'changes', siteId],
    queryFn: () => apiClient.get<{
      hasChanges: boolean
      message?: string
      latestDate?: string
      previousDate?: string
      newDomains?: string[]
      lostDomains?: string[]
      currentCount?: number
      previousCount?: number
    }>(`/api/sites/${siteId}/backlinks/changes`),
    enabled: !!siteId,
    staleTime: 30 * 60 * 1000,
  })
}

/** Anchor text distribution */
export function useBacklinksAnchors(siteId: string) {
  return useQuery({
    queryKey: ['backlinks', 'anchors', siteId],
    queryFn: () => apiClient.get<{
      anchors: AnchorData[]
    }>(`/api/sites/${siteId}/backlinks/anchors`),
    enabled: !!siteId,
    staleTime: 30 * 60 * 1000,
  })
}

// ── Phase 8: Backlinks Expansion ─────────────────────────────

export interface BacklinksTimeseriesItem {
  date: string
  rank: number
  backlinks: number
  referring_domains: number
  referring_main_domains: number
}

export interface SpamScoreResult {
  target: string
  spam_score: number
}

export interface BacklinkCompetitor {
  domain: string
  rank: number
  intersections: number
}

/** Backlinks trends over time (managed tier) */
export function useBacklinksTimeseries(siteId: string) {
  return useQuery({
    queryKey: ['backlinks', 'timeseries', siteId],
    queryFn: () => apiClient.get<{
      items: BacklinksTimeseriesItem[]
    }>(`/api/sites/${siteId}/backlinks/timeseries`),
    enabled: !!siteId,
    staleTime: 30 * 60 * 1000,
  })
}

/** Spam scores for referring domains (managed tier) */
export function useSpamScores(siteId: string) {
  return useQuery({
    queryKey: ['backlinks', 'spam', siteId],
    queryFn: () => apiClient.get<{
      scores: SpamScoreResult[]
    }>(`/api/sites/${siteId}/backlinks/spam-scores`),
    enabled: !!siteId,
    staleTime: 30 * 60 * 1000,
  })
}

// ── Deploy 3: Backlinks History + Domain Pages ──────────────

export interface BacklinksHistoryItem {
  date: string
  rank: number
  backlinks: number
  new_backlinks: number
  lost_backlinks: number
  referring_domains: number
  new_referring_domains: number
  lost_referring_domains: number
}

export interface DomainPageItem {
  page: string
  title: string | null
  backlinks: number
  rank: number
  referring_domains: number
  dofollow: number
  nofollow: number
}

/** Backlinks history with new/lost breakdown (managed tier) */
export function useBacklinksHistory(siteId: string) {
  return useQuery({
    queryKey: ['backlinks', 'history', siteId],
    queryFn: () => apiClient.get<{
      items: BacklinksHistoryItem[]
    }>(`/api/sites/${siteId}/backlinks/history`),
    enabled: !!siteId,
    staleTime: 30 * 60 * 1000,
  })
}

/** Pages with most backlinks for a domain (managed tier) */
export function useDomainPages(siteId: string) {
  return useQuery({
    queryKey: ['backlinks', 'pages', siteId],
    queryFn: () => apiClient.get<{
      pages: DomainPageItem[]
    }>(`/api/sites/${siteId}/backlinks/pages`),
    enabled: !!siteId,
    staleTime: 30 * 60 * 1000,
  })
}

/** Domains sharing backlink sources (managed tier) */
export function useBacklinkCompetitors(siteId: string) {
  return useQuery({
    queryKey: ['backlinks', 'competitors', siteId],
    queryFn: () => apiClient.get<{
      competitors: BacklinkCompetitor[]
    }>(`/api/sites/${siteId}/backlinks/competitors`),
    enabled: !!siteId,
    staleTime: 30 * 60 * 1000,
  })
}

/** Invalidate backlinks cache and re-fetch fresh data */
export function useRefreshBacklinks(siteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => apiClient.post<{
      configured: boolean
      summary: BacklinkSummary | null
    }>(`/api/sites/${siteId}/backlinks/refresh`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backlinks', 'summary', siteId] })
      queryClient.invalidateQueries({ queryKey: ['backlinks', 'domains', siteId] })
      queryClient.invalidateQueries({ queryKey: ['backlinks', 'anchors', siteId] })
    },
  })
}
